import React, { useEffect, useLayoutEffect, useState } from 'react';

import { useResourceContext } from 'react-admin';
import { useLocalStorage } from 'usehooks-ts';

const DATA_ATTR_RESIZE = 'data-resizable';
const MIN_COLUMN_WIDTH = 50;
const RA_COLUMN_CLASS_PREFIX = 'resize-';
const RA_HEADER_COLUMN_CLASS_PREFIX = 'column-';
const COLUMN_WIDTHS_STORAGE_PREFIX = 'kubera.columnWidths.';

type ColumnWidth = Record<string, number>;

const RESIZE_HANDLER_STYLES = `
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 16px;
  cursor: col-resize;
  z-index: 1;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.3;
  transition: opacity 0.15s;
`;

const RESIZE_HANDLER_SVG = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="4"
    height="25"
    viewBox="0 0 4 25"
    style="pointer-events: none"
  >
    <rect x="1" y="0" width="1" height="25" rx="1" fill="currentColor" />
  </svg>
`;

const createResizeHandler = () => {
  const handler = document.createElement('span');
  handler.setAttribute(DATA_ATTR_RESIZE, 'true');
  handler.style.cssText = RESIZE_HANDLER_STYLES;
  handler.innerHTML = RESIZE_HANDLER_SVG;
  return handler;
};

function setThWidth(element: HTMLElement, width: number): void {
  element.style.setProperty('width', `${width}px`, 'important');
  element.style.setProperty('min-width', `${width}px`, 'important');
  element.style.setProperty('max-width', `${width}px`, 'important');
}

function setTdWidth(element: HTMLElement, width: number): void {
  element.style.setProperty('width', `${width}px`, 'important');
  element.style.setProperty('min-width', `${width}px`, 'important');
  element.style.setProperty('max-width', `${width}px`, 'important');
}

const getColumnClassFromTh = (th: HTMLTableCellElement): string | null => {
  const columnClass = Array.from(th.classList).find((c) => c.startsWith(RA_COLUMN_CLASS_PREFIX));
  return columnClass ?? null;
};

const getColumnNameFromClass = (columnClass: string): string =>
  columnClass.replace(RA_COLUMN_CLASS_PREFIX, '');

const isRenderedColumn = (th: HTMLTableCellElement): boolean =>
  Array.from(th.classList).some((c) => c.startsWith(RA_HEADER_COLUMN_CLASS_PREFIX));

/**
 * Определяет, какие колонки можно ресайзить.
 *
 * Варианты:
 * - `true` — разрешить ресайз для всех колонок
 * - `string[]` — список колонок, которые можно ресайзить
 *
 * В массиве нужно передавать **имена колонок без префикса `resize-`**,
 * так как внутри хука он добавляется автоматически.
 *
 * Пример:
 * ```ts
 * useColumnResize(ref, true); // все колонки ресайзятся
 *
 * useColumnResize(ref, ['name', 'email']);
 * // будут ресайзиться колонки с классами:
 * // resize-name, resize-email
 * ```
 */
export type ResizableColumns = string[] | true;

export function useColumnResize(
  containerRef: React.RefObject<HTMLDivElement>,
  resizableColumns: ResizableColumns | undefined,
  storeKey?: string,
) {
  const resource = useResourceContext();
  const resolvedStoreKey = storeKey ?? `${resource}.columnWidths`;
  const [savedWidths, setSavedWidths] = useLocalStorage<ColumnWidth>(
    `${COLUMN_WIDTHS_STORAGE_PREFIX}${resolvedStoreKey}`,
    {},
  );

  const [columnsKey, setColumnsKey] = useState(0);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      const isOwnMutation = mutations.every((m) =>
        Array.from(m.addedNodes).every(
          (node) => node instanceof HTMLElement && node.hasAttribute(DATA_ATTR_RESIZE),
        ),
      );

      if (isOwnMutation) {
        return;
      }

      setColumnsKey((k) => k + 1);
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const isColumnResizable = (columnClass: string) => {
    if (resizableColumns === true) {
      return true;
    }
    if (Array.isArray(resizableColumns)) {
      return resizableColumns.some(
        (source) => columnClass === `${RA_COLUMN_CLASS_PREFIX}${source}`,
      );
    }
    return false;
  };

  useLayoutEffect(() => {
    if (!resizableColumns || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const table = container.querySelector('table');

    if (!table) {
      return;
    }

    const thList = Array.from(
      container.querySelectorAll<HTMLTableCellElement>('thead th, thead td'),
    );

    if (!thList.some(isRenderedColumn)) {
      return;
    }

    table.style.tableLayout = 'fixed';
    table.style.width = 'max-content';
    container.style.overflowX = 'auto';

    const cleanups: (() => void)[] = [];

    thList.forEach((th) => {
      const columnClass = getColumnClassFromTh(th);
      if (!columnClass || !isColumnResizable(columnClass)) {
        return;
      }

      const columnName = getColumnNameFromClass(columnClass);
      const savedWidth = savedWidths[columnName];
      const matchingTds = container.querySelectorAll<HTMLElement>(`tbody td.${columnClass}`);

      if (savedWidth) {
        setThWidth(th, savedWidth);
        matchingTds.forEach((td) => setTdWidth(td, savedWidth));
      }

      if (th.querySelector(`[${DATA_ATTR_RESIZE}]`)) {
        return;
      }

      const isStickyTh = getComputedStyle(th).position === 'sticky';
      if (!isStickyTh) {
        th.style.position = 'relative';
      }
      const resizeHandler = createResizeHandler();
      th.appendChild(resizeHandler);

      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = th.offsetWidth;

        const onMouseMove = (e: MouseEvent) => {
          const delta = e.clientX - startX;
          const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + delta);
          setThWidth(th, newWidth);
          matchingTds.forEach((td) => setTdWidth(td, newWidth));
        };

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          setSavedWidths((prev) => ({ ...prev, [columnName]: th.offsetWidth }));
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      resizeHandler.addEventListener('mousedown', onMouseDown);

      cleanups.push(() => {
        resizeHandler.removeEventListener('mousedown', onMouseDown);
        resizeHandler.remove();
        if (!isStickyTh) {
          th.style.position = '';
        }
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [resizableColumns, containerRef, columnsKey]);
}
